import React, { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { DatePicker, Select, Button, Table, Space, Typography } from "antd";
import { CopyOutlined, DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import ExcelJS from "exceljs";

import "./OrdersProductsComponent.module.css";
import { ordersActions } from "../../../store/slices/orders-slices";
import { productGroupsActions } from "../../../store/slices/settings-slices";
import { ordersService } from "../../../services";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const OrdersProductsComponent = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { productsOrderByDate, status } = useSelector((state) => state.orders);
  const { productGroups } = useSelector((state) => state.productGroups);
  const { dateRange } = useSelector((state) => state.orders);
  const [groupingMode, setGroupingMode] = useState("flat");
  const [loadingKitchenSheet, setLoadingKitchenSheet] = useState(false);

  const isLoading = useMemo(() => status === "Loading", [status]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}.${month}.${year}`;
  };

  const getDefaultDateRange = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const formatDateForInput = (date) => {
      return date.toISOString().split("T")[0];
    };

    return {
      startDate: formatDateForInput(today),
      endDate: formatDateForInput(tomorrow),
    };
  };

  const handleCopyToClipboard = async () => {
    if (!productsOrderByDate || productsOrderByDate.length === 0) {
      toast.error(t("No products data available for the selected date"));
      return;
    }

    try {
      const text = productsOrderByDate
        .map((product) => {
          const name = product.name?.[i18n.language] || "";
          const weight = product.weight || "";
          return `${name}  ${weight}`;
        })
        .join("\n");

      await navigator.clipboard.writeText(text);
      toast.success(t("Products list copied to clipboard"));
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      toast.error(t("Failed to copy to clipboard"));
    }
  };

  const handleClickXls = async () => {
    if (!productsOrderByDate || productsOrderByDate.length === 0) {
      alert(t("No products data available for the selected date"));
      return;
    }

    // Create a new workbook
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "GWT ERP";
    workbook.lastModifiedBy = "GWT ERP";
    workbook.created = new Date();
    workbook.modified = new Date();

    // Add Products worksheet
    const productsWorksheet = workbook.addWorksheet("Products");

    // Set column widths for Products worksheet
    productsWorksheet.columns = [
      { width: 30 }, // Product
      { width: 15 }, // Weight (g)
    ];

    // Add title row for Products
    const titleText =
      dateRange.startDate && dateRange.endDate
        ? `Products from: ${formatDate(dateRange.startDate)} to: ${formatDate(
            dateRange.endDate
          )}`
        : "Products";
    const productsTitleRow = productsWorksheet.addRow([titleText]);
    productsTitleRow.font = { bold: true, size: 14 };
    productsWorksheet.mergeCells("A1:B1");
    productsWorksheet.getCell("A1").alignment = { horizontal: "center" };
    productsTitleRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "E2EFDA" },
      };
    });

    // Add header row for Products
    const productsHeaderRow = productsWorksheet.addRow([
      "Product",
      "Weight (g)",
    ]);
    productsHeaderRow.font = { bold: true, color: { argb: "FFFFFF" } };
    productsHeaderRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "4472C4" },
      };
      cell.alignment = { horizontal: "center" };
    });

    // Add products data
    productsOrderByDate.forEach((product) => {
      const currentLanguage = i18n.language;
      const displayName = product.name?.[currentLanguage] || "";

      const productRow = productsWorksheet.addRow([
        displayName,
        product.weight || "",
      ]);
      productRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFE5CC" },
        };
      });
    });

    // Add borders to all cells
    productsWorksheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    });

    // Generate the Excel file
    const buffer = await workbook.xlsx.writeBuffer();

    // Create a Blob and download it
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const filename =
      dateRange.startDate && dateRange.endDate
        ? `products_${formatDate(dateRange.startDate)}-${formatDate(
            dateRange.endDate
          )}.xlsx`
        : "products.xlsx";
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // Initialize date range and fetch products data
  useEffect(() => {
    if (!dateRange.startDate || !dateRange.endDate) {
      const initialRange = getDefaultDateRange();
      dispatch(ordersActions.setDateRange(initialRange));
    }
  }, [dispatch, dateRange.startDate, dateRange.endDate]);

  // Fetch products data when date range changes
  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      dispatch(
        ordersActions.getProductsOrderByDate({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        })
      );
    }
  }, [dispatch, dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    dispatch(productGroupsActions.getAllProductGroups());
  }, [dispatch]);

  const handleGroupChange = (value) => {
    if (value === "no_grouping") {
      setGroupingMode("flat");
      dispatch(ordersActions.setProductsQueryParams({ groupId: undefined }));
    } else if (value) {
      setGroupingMode("grouped");
      dispatch(ordersActions.setProductsQueryParams({ groupId: value }));
    } else {
      setGroupingMode("grouped");
      dispatch(ordersActions.setProductsQueryParams({ groupId: undefined }));
    }
    dispatch(ordersActions.changeTrigger());
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      dispatch(
        ordersActions.setDateRange({
          startDate: dates[0].format("YYYY-MM-DD"),
          endDate: dates[1].format("YYYY-MM-DD"),
        })
      );
    }
  };

  // Prepare group options for Select
  const groupOptions = useMemo(() => {
    const options = [
      { label: "No grouping", value: "no_grouping" },
      { label: "All groups", value: "" },
    ];
    if (productGroups) {
      productGroups.forEach((group) => {
        options.push({
          label: group.name?.en || group.name?.[i18n.language] || "",
          value: group.id,
        });
      });
    }
    return options;
  }, [productGroups, i18n.language]);

  // Prepare sort options
  const sortOptions = [
    { label: "Created date ↓", value: "createdAt_desc" },
    { label: "Created date ↑", value: "createdAt_asc" },
    { label: "Default", value: "none" },
  ];

  // Prepare grouped data for table
  const tableData = useMemo(() => {
    if (!productsOrderByDate || productsOrderByDate.length === 0) return [];

    if (groupingMode === "grouped") {
      // If backend already returns grouped shape
      if (Array.isArray(productsOrderByDate[0]?.products)) {
        const result = [];
        productsOrderByDate.forEach((group) => {
          result.push({
            key: `group-${group.groupId || group.group?.id}`,
            isGroupHeader: true,
            name:
              group.groupName?.[i18n.language] ||
              group.group?.name?.[i18n.language] ||
              "",
          });
          (group.products || []).forEach((product) => {
            result.push({
              key: product.id || product.productId,
              name: product.name?.[i18n.language] || "",
              weight: product.weight || "",
              price: product.price || "",
            });
          });
        });
        return result;
      }

      // Fallback: group on frontend
      const map = new Map();
      productsOrderByDate.forEach((item) => {
        const groupId = item.groupId || item.group?.id || "unknown";
        const groupName = item.group?.name || {};
        if (!map.has(groupId)) {
          map.set(groupId, { groupId, groupName, products: [] });
        }
        map.get(groupId).products.push(item);
      });

      const result = [];
      Array.from(map.values()).forEach((group) => {
        result.push({
          key: `group-${group.groupId}`,
          isGroupHeader: true,
          name: group.groupName?.[i18n.language] || "",
        });
        group.products.forEach((product) => {
          result.push({
            key: product.id || product.productId,
            name: product.name?.[i18n.language] || "",
            weight: product.weight || "",
            price: product.price || "",
          });
        });
      });
      return result;
    }

    // Flat mode
    return productsOrderByDate.map((product) => ({
      key: product.id || product.productId,
      name: product.name?.[i18n.language] || "",
      weight: product.weight || "",
      price: product.price || "",
    }));
  }, [productsOrderByDate, groupingMode, i18n.language]);

  // Table columns
  const columns = [
    {
      title: t("settings-menu-title-products"),
      dataIndex: "name",
      key: "name",
      width: "60%",
      render: (text, record) => {
        if (record.isGroupHeader) {
          return (
            <Text strong style={{ color: "#1890ff" }}>
              {text}
            </Text>
          );
        }
        return text;
      },
    },
    {
      title: t("Weight"),
      dataIndex: "weight",
      key: "weight",
      width: "20%",
      align: "center",
      render: (text, record) => (record.isGroupHeader ? "" : text),
    },
    {
      title: t("modal-price"),
      dataIndex: "price",
      key: "price",
      width: "20%",
      align: "center",
      render: (text, record) => (record.isGroupHeader ? "" : text),
    },
  ];

  const handleClickXlsForKitchen = async () => {
    try {
      setLoadingKitchenSheet(true);
      const response = await ordersService.getKitchenSheetData(dateRange.startDate, dateRange.endDate);

      console.log(response);

      if (!response || response.length === 0) {
        toast.warning(t("No kitchen data available for the selected date range"));
        return;
      }

      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'GWT ERP';
      workbook.created = new Date();

      // Helper function to add borders to all cells
      const addBordersToAllCells = (worksheet) => {
        worksheet.eachRow((row) => {
          row.eachCell((cell) => {
            cell.border = {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' }
            };
          });
        });
      };

      // Helper function to calculate nutritional totals from ingredients
      const calculateNutrition = (ingredients) => {
        const totals = {
          proteins: 0,
          carbohydrates: 0,
          fats: 0,
          energy: 0
        };

        if (ingredients && Array.isArray(ingredients)) {
          ingredients.forEach(ingredient => {
            totals.proteins += parseFloat(ingredient.proteins || 0);
            totals.carbohydrates += parseFloat(ingredient.carbohydrates || 0);
            totals.fats += parseFloat(ingredient.fats || 0);
            totals.energy += parseFloat(ingredient.energy || 0);
          });
        }

        return {
          proteins: Math.round(totals.proteins),
          carbohydrates: Math.round(totals.carbohydrates),
          fats: Math.round(totals.fats),
          energy: Math.round(totals.energy)
        };
      };

      // Create a worksheet for each date
      response.forEach(dateData => {
        const sheetName = formatDate(dateData.date);
        const worksheet = workbook.addWorksheet(sheetName);

        // Set up columns
        worksheet.columns = [
          { key: 'name', width: 30 },
          { key: 'dishName', width: 30 },
          { key: 'type', width: 20 },
          { key: 'ingredient', width: 70 },
          { key: 'weightNetto', width: 12 },
          { key: 'protein', width: 12 },
          { key: 'carbs', width: 12 },
          { key: 'fat', width: 12 },
          { key: 'calories', width: 12 },
        ];

        // Add title row
        const titleRow = worksheet.addRow([`Kitchen Sheet - ${sheetName}`, '', '', '', '', '', '', '', '']);
        titleRow.font = { bold: true, size: 14 };
        worksheet.mergeCells(`A1:I1`);
        worksheet.getCell('A1').alignment = { horizontal: 'center' };
        titleRow.eachCell(cell => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'E6F4FF' }
          };
        });

        // Add header row
        const headerRow = worksheet.addRow(['Name', 'Dish Name', 'Type', 'Ingredient', 'Weight Netto', 'Protein', 'Carbs', 'Fat', 'CALORIES']);
        headerRow.font = { bold: true, color: { argb: 'FFFFFF' } };
        headerRow.eachCell(cell => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: '70AD47' }
          };
          cell.alignment = { horizontal: 'center' };
        });

        // Add data rows grouped by clients
        dateData.clients.forEach(client => {
          // Get client name from first order (assuming all orders have same client info)
          const clientName = client.clientName || 'Unknown Client';
          
          // Add client name row spanning across columns
          const clientNameRow = worksheet.addRow([clientName, '', '', '', '', '', '', '', '']);
          clientNameRow.font = { bold: true, size: 12 };
          clientNameRow.eachCell(cell => {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'E6F4FF' }
            };
          });

          // Process each order's dishes
          client.orders.forEach((order, index) => {
            const currentLanguage = i18n.language;
            const dishName = order.dishName?.[currentLanguage] || 
                           order.dishName?.en || 
                           order.dishName?.ru || 
                           order.dishName?.ge || '';
            
            const mealType = order.meal?.name?.[currentLanguage] || 
                           order.meal?.name?.en || 
                           order.meal?.name?.ru || 
                           order.meal?.name?.ge || '';

            // Get all product names
            const productNames = (order.ingredients || [])
              .map(product => 
                `${product.name?.[currentLanguage] || 
                  product.name?.en || 
                  product.name?.ru || 
                  product.name?.ge || ''} - ${product.weight}g`
              )
              .filter(Boolean)
              .join('\n');

            // Calculate nutritional values
            const nutrition = calculateNutrition(order.ingredients);

            // Add dish row
            const dishRow = worksheet.addRow({
              name: index === 0 ? '' : '', // Empty for subsequent dishes of same client
              dishName: dishName,
              type: mealType,
              ingredient: productNames,
              protein: nutrition.proteins,
              carbs: nutrition.carbohydrates,
              fat: nutrition.fats,
              calories: nutrition.energy,
              weightNetto: order.weightNetto || '',
            });

            // Style dish row
            dishRow.eachCell((cell, colNumber) => {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFFFFF' }
              };
              
              // Center align nutritional columns
              if (colNumber >= 4) {
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
              }
            });

            dishRow.getCell('type').alignment = { horizontal: 'center', vertical: 'middle' };

            // Enable text wrapping for ingredient cell
            dishRow.getCell('ingredient').alignment = { 
              wrapText: true,
              vertical: 'top'
            };
          });

          // Add an empty row as separator between clients
          worksheet.addRow(['', '', '', '', '', '', '', '', '']);
        });

        // Add borders to all cells
        addBordersToAllCells(worksheet);
      });

      // Generate and download the Excel file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kitchen_sheet_${formatDate(dateRange.startDate)}_${formatDate(dateRange.endDate)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(t("Kitchen sheet generated successfully"));
    } catch (error) {
      console.error("Failed to get kitchen sheet data:", error);
      toast.error(t("Failed to get kitchen sheet data"));
    } finally {
      setLoadingKitchenSheet(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        padding: "16px",
        boxSizing: "border-box",
      }}
    >
      {/* Controls Bar */}
      <Space
        direction="horizontal"
        size="middle"
        style={{
          marginBottom: 16,
          padding: "12px 16px",
          background: "#fff",
          borderRadius: 8,
          border: "1px solid #e6e8ee",
          flexWrap: "wrap",
        }}
      >
        <RangePicker
          value={
            dateRange.startDate && dateRange.endDate
              ? [dayjs(dateRange.startDate), dayjs(dateRange.endDate)]
              : null
          }
          onChange={handleDateRangeChange}
          format="YYYY-MM-DD"
          style={{ width: 280 }}
        />

        <Select
          defaultValue="no_grouping"
          options={groupOptions}
          onChange={handleGroupChange}
          style={{ width: 200 }}
        />
      </Space>

      {/* Table */}
      <div>
        <Table
          dataSource={tableData}
          columns={columns}
          pagination={false}
          loading={isLoading}
          bordered
          size="small"
          rowClassName={(record) =>
            record.isGroupHeader ? "group-header-row" : ""
          }
          locale={{ emptyText: t("no-data") }}
          scroll={{ y: "calc(89vh - 300px)" }}
          footer={() => (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 16px",
                background: "#fff",
                borderTop: "1px solid #ddd",
                borderRadius: 8,
                flexWrap: "wrap",
                gap: "12px",
                position: "sticky",
                bottom: 0,
                zIndex: 1000,
              }}
            >
              <Space>
                <Button
                  type="primary"
                  icon={<CopyOutlined />}
                  onClick={handleCopyToClipboard}
                >
                  Copy
                </Button>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleClickXls}
                  style={{ background: "#52c41a", borderColor: "#52c41a" }}
                >
                  Export XLS
                </Button>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleClickXlsForKitchen}
                  loading={loadingKitchenSheet}
                  style={{ background: "#52c41a", borderColor: "#52c41a" }}
                >
                  Export XLS For Kitchen
                </Button>
              </Space>

              <Text strong style={{ fontSize: 16 }}>
                {t("total-amount")}: 0
              </Text>
            </div>
          )}
        />
      </div>
    </div>
  );
};

export { OrdersProductsComponent };
